/**
 * Fact-Sheet read-only extension blocks — Strengths & Needs, Risk Assessment,
 * Client Info summary, Consents summary, plus body-check & risk-domain trends.
 *
 * All sections fetch in parallel and degrade gracefully when missing.
 * Owned by ClientFactSheet — kept here so the main fact-sheet file stays slim.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Heart, ShieldCheck, ShieldAlert, Users, Sparkles, ClipboardList,
  TrendingUp, FileSignature, Stethoscope, Pill, Home, Loader2, Download,
} from "lucide-react";

const RISK_COLOR = {
  low: "#22C55E", moderate: "#F59E0B", high: "#EF4444", "": "#94A3B8",
};

const RISK_DOMAINS = [
  { k: "fall_risk", l: "Falls" },
  { k: "elopement_risk", l: "Elopement" },
  { k: "choking_aspiration_risk", l: "Choking" },
  { k: "self_harm_risk", l: "Self-harm" },
  { k: "suicide_risk", l: "Suicide" },
  { k: "aggression_risk", l: "Aggression" },
  { k: "medication_self_admin_risk", l: "Medication self-admin" },
  { k: "skin_breakdown_risk", l: "Skin breakdown" },
  { k: "infection_risk", l: "Infection" },
  { k: "abuse_neglect_risk", l: "Abuse / neglect" },
  { k: "financial_exploitation_risk", l: "Financial" },
];

const FINDING_COLOR = {
  normal: "#22C55E", redness: "#F59E0B", bruise: "#A16207",
  cut: "#EF4444", rash: "#EC4899", swelling: "#F59E0B",
  pressure_sore: "#B91C1C", wound: "#B91C1C", scab: "#92400E", other: "#64748B",
};

function Section({ icon: Icon, title, accent = "#0F172A", danger = false, children, action }) {
  return (
    <div
      className="card-surface p-4"
      style={danger ? { borderColor: "#FCA5A5", background: "#FEF2F2" } : {}}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: danger ? "#B91C1C" : accent }} />
          <h3
            className="text-sm font-semibold uppercase tracking-wide"
            style={{ color: danger ? "#7F1D1D" : "#0F172A" }}
          >
            {title}
          </h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function PillBadge({ children, color = "#64748B" }) {
  return (
    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium"
      style={{ background: color + "1a", color }}>
      {children}
    </span>
  );
}

/* ============== Strengths & Needs summary ============== */
function StrengthsNeedsBlock({ data }) {
  const hasAny = (data?.strengths?.length || 0) > 0 ||
    (data?.interests?.length || 0) > 0 ||
    ["physical_needs", "emotional_needs", "cognitive_needs", "medical_needs", "safety_needs",
      "short_term_goals", "long_term_goals"].some((k) => (data?.[k] || "").trim());

  if (!hasAny) {
    return <p className="text-sm text-clinical-subtext">Not assessed yet.</p>;
  }
  return (
    <div className="space-y-3" data-testid="fs-strengths-block">
      {(data.strengths || []).length > 0 && (
        <div>
          <div className="label-eyebrow mb-1">Strengths</div>
          <div className="flex flex-wrap gap-1">
            {data.strengths.map((s) => <PillBadge key={s} color="#16A34A">{s}</PillBadge>)}
          </div>
        </div>
      )}
      {(data.interests || []).length > 0 && (
        <div>
          <div className="label-eyebrow mb-1">Interests</div>
          <div className="flex flex-wrap gap-1">
            {data.interests.map((s) => <PillBadge key={s} color="#7C3AED">{s}</PillBadge>)}
          </div>
        </div>
      )}
      {(data.support_network || []).length > 0 && (
        <div>
          <div className="label-eyebrow mb-1">Support network</div>
          <div className="flex flex-wrap gap-1">
            {data.support_network.map((s) => <PillBadge key={s} color="#0EA5E9">{s}</PillBadge>)}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 text-sm">
        {[
          ["medical_needs", "Medical needs"],
          ["safety_needs", "Safety needs"],
          ["physical_needs", "Physical needs"],
          ["cognitive_needs", "Cognitive needs"],
          ["short_term_goals", "Short-term goals"],
          ["long_term_goals", "Long-term goals"],
        ].filter(([k]) => (data[k] || "").trim()).map(([k, l]) => (
          <div key={k} className="rounded-lg bg-white/70 p-2.5">
            <div className="label-eyebrow mb-0.5">{l}</div>
            <p className="text-clinical-text">{data[k]}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== Risk Assessment summary + heat-strip ============== */
function RiskBlock({ data }) {
  if (!data || !data.client_id) {
    return <p className="text-sm text-clinical-subtext">No risk assessment on file.</p>;
  }
  const highCount = RISK_DOMAINS.filter((d) => data[d.k] === "high").length;
  const modCount = RISK_DOMAINS.filter((d) => data[d.k] === "moderate").length;
  const overall = data.overall_risk;
  const anyDomainSet = RISK_DOMAINS.some((d) => data[d.k]);
  const hasContent = anyDomainSet || (data.summary || "").trim() || overall;
  if (!hasContent) {
    return <p className="text-sm text-clinical-subtext">No risk assessment on file.</p>;
  }

  return (
    <div className="space-y-3" data-testid="fs-risk-block">
      <div className="flex flex-wrap items-center gap-2">
        {overall && (
          <PillBadge color={RISK_COLOR[overall]}>OVERALL: {overall}</PillBadge>
        )}
        {highCount > 0 && (
          <PillBadge color="#EF4444">{highCount} high</PillBadge>
        )}
        {modCount > 0 && (
          <PillBadge color="#F59E0B">{modCount} moderate</PillBadge>
        )}
        {data.review_due_on && (
          <span className="text-[11px] text-clinical-subtext">
            Review due {data.review_due_on}
          </span>
        )}
      </div>
      {data.summary && (
        <p className="rounded-lg bg-white/70 p-2.5 text-sm text-clinical-text">
          {data.summary}
        </p>
      )}
      {/* Domain heat strip */}
      <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
        {RISK_DOMAINS.filter((d) => data[d.k]).map((d) => (
          <div key={d.k} className="flex items-center gap-2 rounded-md px-2 py-1 text-xs"
            style={{ background: RISK_COLOR[data[d.k]] + "1a" }}>
            <span className="h-2 w-2 rounded-full" style={{ background: RISK_COLOR[data[d.k]] }} />
            <span className="truncate text-clinical-text">{d.l}</span>
            <span className="ml-auto font-medium uppercase"
              style={{ color: RISK_COLOR[data[d.k]] }}>
              {data[d.k]}
            </span>
          </div>
        ))}
      </div>
      {data.updated_at && (
        <div className="text-[10px] text-clinical-subtext">
          Last updated {new Date(data.updated_at).toLocaleDateString()}
          {data.updated_by_name && ` · ${data.updated_by_name}`}
        </div>
      )}
    </div>
  );
}

/* ============== Client Info summary (care providers + POA) ============== */
function ClientInfoBlock({ data }) {
  if (!data || !data.client_id) {
    return <p className="text-sm text-clinical-subtext">No additional information captured.</p>;
  }
  const blocks = [
    { l: "PCP", icon: Stethoscope, name: data.pcp_name, phone: data.pcp_phone },
    { l: "Pharmacy", icon: Pill, name: data.pharmacy_name, phone: data.pharmacy_phone },
    { l: "Preferred hospital", icon: Heart, name: data.preferred_hospital, phone: "" },
    { l: "Hospice", icon: Heart, name: data.hospice_provider, phone: data.hospice_phone },
    { l: "POA", icon: ShieldCheck, name: data.poa_name + (data.poa_type ? ` (${data.poa_type})` : ""),
      phone: data.poa_phone, show: !!(data.has_poa || data.poa_name) },
    { l: "Legal guardian", icon: ShieldCheck, name: data.legal_guardian_name, phone: data.legal_guardian_phone },
    { l: "Living arrangement", icon: Home, name: (data.living_arrangement || "").replace(/_/g, " "), phone: "" },
  ].filter((b) => b.show !== false && ((b.name || "").trim() || (b.phone || "").trim()));

  if (blocks.length === 0 && !data.recent_hospitalizations && !data.insurance_primary) {
    return <p className="text-sm text-clinical-subtext">No additional information captured.</p>;
  }

  return (
    <div className="space-y-3" data-testid="fs-client-info-block">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {blocks.map((b) => (
          <div key={b.l} className="rounded-lg border border-clinical-border bg-white p-2.5">
            <div className="flex items-center gap-2 text-xs text-clinical-subtext">
              <b.icon size={12} />
              <span className="uppercase tracking-wide">{b.l}</span>
            </div>
            <div className="mt-0.5 text-sm font-medium text-clinical-text">{b.name || "—"}</div>
            {b.phone && (
              <a href={`tel:${b.phone}`} className="text-xs text-clinical-primary hover:underline">
                {b.phone}
              </a>
            )}
          </div>
        ))}
      </div>
      {data.insurance_primary && (
        <div className="text-xs text-clinical-subtext">
          <span className="font-medium text-clinical-text">Insurance: </span>
          {data.insurance_primary}{data.insurance_primary_id ? ` · #${data.insurance_primary_id}` : ""}
        </div>
      )}
      {data.recent_hospitalizations && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs">
          <div className="label-eyebrow text-amber-900 mb-0.5">Recent hospitalizations</div>
          <p className="text-amber-900 whitespace-pre-line">{data.recent_hospitalizations}</p>
        </div>
      )}
    </div>
  );
}

/* ============== Consents summary + PDF download ============== */
function ConsentsBlock({ consents, clientId, clientName }) {
  const active = (consents || []).filter((c) => !c.revoked);
  const revoked = (consents || []).filter((c) => c.revoked);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/clients/${clientId}/consents.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consents-${(clientName || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // toast handled upstream
    }
  };

  return (
    <Section
      icon={FileSignature} title="Consents" accent="#7C3AED"
      action={
        (consents || []).length > 0 ? (
          <button
            type="button"
            onClick={downloadPdf}
            className="inline-flex items-center gap-1 rounded-lg border border-clinical-border bg-white px-2 py-1 text-xs font-medium text-clinical-text hover:border-clinical-primary"
            data-testid="fs-consents-pdf-btn"
          >
            <Download size={12} /> PDF packet
          </button>
        ) : null
      }
    >
      <div data-testid="fs-consents-block">
        {!consents || consents.length === 0 ? (
          <p className="text-sm text-clinical-subtext">No consents captured yet.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <PillBadge color="#16A34A">{active.length} active</PillBadge>
              {revoked.length > 0 && <PillBadge color="#94A3B8">{revoked.length} revoked</PillBadge>}
            </div>
            <ul className="space-y-1.5">
              {(consents || []).slice(0, 6).map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 rounded-lg bg-white/70 p-2 text-sm">
                  <div>
                    <div className={`font-medium text-clinical-text ${c.revoked ? "line-through opacity-60" : ""}`}>
                      {c.label}
                    </div>
                    <div className="text-[11px] text-clinical-subtext">
                      {c.signer_name} ({c.signer_relationship}) ·{" "}
                      {new Date(c.signature?.signed_at || c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {c.revoked && <PillBadge color="#EF4444">revoked</PillBadge>}
                </li>
              ))}
            </ul>
            {(consents || []).length > 6 && (
              <Link to={`/admin/clients?client=${clientId}&tab=consents`} className="text-xs text-clinical-primary hover:underline">
                View all {consents.length} consents →
              </Link>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

/* ============== Body Check trend ============== */
function BodyCheckTrend({ entries }) {
  if (!entries || entries.length === 0) {
    return <p className="text-sm text-clinical-subtext">No body checks logged in the last 30 days.</p>;
  }
  // Summarise findings count by area + worst finding seen
  const areaWorst = {};
  entries.forEach((e) => {
    (e.findings || []).forEach((f) => {
      const cur = areaWorst[f.area];
      const sev = ["normal", "redness", "scab", "bruise", "rash", "abrasion",
        "swelling", "cut", "wound", "pressure_sore", "other"].indexOf(f.finding);
      if (!cur || sev > cur.sev) {
        areaWorst[f.area] = { finding: f.finding, sev };
      }
    });
  });
  const integrityCounts = entries.reduce((acc, e) => {
    acc[e.overall_skin_integrity] = (acc[e.overall_skin_integrity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-3" data-testid="fs-body-check-block">
      <div className="flex flex-wrap gap-1.5">
        <PillBadge color="#0EA5E9">{entries.length} checks</PillBadge>
        {integrityCounts.compromised > 0 && (
          <PillBadge color="#EF4444">{integrityCounts.compromised} compromised</PillBadge>
        )}
        {integrityCounts.concerns > 0 && (
          <PillBadge color="#F59E0B">{integrityCounts.concerns} concerns</PillBadge>
        )}
        {integrityCounts.intact > 0 && (
          <PillBadge color="#22C55E">{integrityCounts.intact} intact</PillBadge>
        )}
      </div>
      {Object.keys(areaWorst).length > 0 ? (
        <div>
          <div className="label-eyebrow mb-1">Most-affected areas (30d)</div>
          <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
            {Object.entries(areaWorst).map(([area, v]) => (
              <div key={area} className="flex items-center gap-2 rounded-md px-2 py-1 text-xs"
                style={{ background: (FINDING_COLOR[v.finding] || "#64748B") + "1a" }}>
                <span className="h-2 w-2 rounded-full" style={{ background: FINDING_COLOR[v.finding] }} />
                <span className="truncate text-clinical-text">{area.replace(/_/g, " ")}</span>
                <span className="ml-auto text-[10px] font-medium uppercase"
                  style={{ color: FINDING_COLOR[v.finding] }}>
                  {v.finding}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-clinical-subtext">No findings recorded — all skin intact.</p>
      )}
      <div className="text-[10px] text-clinical-subtext">
        Most recent: {new Date(entries[0].recorded_at).toLocaleString()} · {entries[0].caregiver_name}
      </div>
    </div>
  );
}

/* ============== Master read-only extension ============== */
export default function FactSheetExtras({ clientId, clientName }) {
  const [data, setData] = useState({
    strengths: null, risk: null, info: null, consents: [], bodyChecks: [],
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sn, risk, info, consents, bodyChecks] = await Promise.all([
          api.get(`/clients/${clientId}/strengths-needs`).catch(() => ({ data: null })),
          api.get(`/clients/${clientId}/risk-assessment`).catch(() => ({ data: null })),
          api.get(`/clients/${clientId}/client-info`).catch(() => ({ data: null })),
          api.get(`/clients/${clientId}/consents`).catch(() => ({ data: [] })),
          api.get(`/clients/${clientId}/body-check?days=30`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setData({
          strengths: sn.data, risk: risk.data, info: info.data,
          consents: consents.data || [], bodyChecks: bodyChecks.data || [],
          loading: false,
        });
      } catch {
        if (!cancelled) setData((d) => ({ ...d, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (data.loading) {
    return (
      <div className="card-surface p-6 flex items-center gap-2 text-sm text-clinical-subtext">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading assessments…
      </div>
    );
  }

  const overallRisk = data.risk?.overall_risk;
  const dangerRisk = overallRisk === "high";

  return (
    <div className="space-y-4" data-testid="fact-sheet-extras">
      <Section
        icon={dangerRisk ? ShieldAlert : ShieldCheck}
        title="Risk assessment" accent="#EF4444"
        danger={dangerRisk}
      >
        <RiskBlock data={data.risk} />
      </Section>

      <Section icon={TrendingUp} title="Body check (last 30 days)" accent="#0EA5E9">
        <BodyCheckTrend entries={data.bodyChecks} />
      </Section>

      <Section icon={Sparkles} title="Strengths & needs" accent="#16A34A">
        <StrengthsNeedsBlock data={data.strengths} />
      </Section>

      <Section icon={Users} title="Client information" accent="#0EA5E9">
        <ClientInfoBlock data={data.info} />
      </Section>

      <ConsentsBlock consents={data.consents} clientId={clientId} clientName={clientName} />
    </div>
  );
}

// Re-export icons used by parent file pattern (avoid unused-imports warnings)
export { ClipboardList };
